import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import WikiIcon from '../shared/WikiIcon';
import { WIKI_MODAL_COPY } from './wikiModalCopy';

const FOCUSABLE = [
    'a[href]', 'button', 'input', 'textarea', 'select', '[tabindex]', '[contenteditable="true"]',
].join(',');

function focusableIn(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => (
        !el.disabled
        && el.tabIndex >= 0
        && !el.hidden
        && el.getAttribute('aria-hidden') !== 'true'
        && !el.closest('[hidden]')
    ));
}

/**
 * The prototype modal frame (C2 §1.2), portalled to <body>:
 *   backdrop (click → close) · `beforeCard` (the location picker layer — a
 *   SIBLING of the card, rendered before it) · card 600px wide, 56px from the
 *   top, header (title + 26px Close) / scrolling body / right-aligned footer.
 *
 * Real-build additions the prototype lacked (C2 §1.2 last bullet): Escape
 * closes the modal (the open picker swallows its own Escape first), Tab is
 * trapped inside the layer, the first `[data-wk-autofocus]` control is focused
 * on open, and focus returns to the opener on close.
 */
export default function WikiModalShell({ title, onClose, footer, beforeCard, children, testId = 'wiki-modal' }) {
    const rootRef = useRef(null);
    const cardRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const titleId = useId();

    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    // Initial focus + focus restore.
    useEffect(() => {
        const opener = typeof document !== 'undefined' ? document.activeElement : null;
        const card = cardRef.current;
        const target = card && (card.querySelector('[data-wk-autofocus]') || card);
        if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
        return () => {
            if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
                opener.focus({ preventScroll: true });
            }
        };
    }, []);

    // Escape closes the modal unless something inside already handled it
    // (the location picker calls preventDefault + stopPropagation).
    useEffect(() => {
        const onKey = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            event.preventDefault();
            if (onCloseRef.current) onCloseRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    const onKeyDown = (event) => {
        if (event.key !== 'Tab' || event.defaultPrevented) return;
        const nodes = focusableIn(rootRef.current);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;
        const inside = rootRef.current.contains(active);
        if (event.shiftKey && (!inside || active === first || active === cardRef.current)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (!inside || active === last)) {
            event.preventDefault();
            first.focus();
        }
    };

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div ref={rootRef} className="wk-vars wk-modal-root" data-testid={testId} onKeyDown={onKeyDown}>
            <div className="wk-modal-backdrop" data-testid="wiki-modal-backdrop" onClick={onClose} aria-hidden="true" />
            {beforeCard}
            <div
                ref={cardRef}
                className="wk-modal-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                data-testid="wiki-modal-card"
            >
                <div className="wk-modal-head">
                    <h2 id={titleId} className="wk-modal-title wk-t-title">{title}</h2>
                    <button
                        type="button"
                        className="wk-modal-close"
                        title={WIKI_MODAL_COPY.close}
                        aria-label={WIKI_MODAL_COPY.close}
                        onClick={onClose}
                    >
                        <WikiIcon name="x" size={15} />
                    </button>
                </div>
                <div className="wk-modal-body" data-testid="wiki-modal-body">{children}</div>
                <div className="wk-modal-foot">
                    <button type="button" className="wk-btn wk-btn--ghost" onClick={onClose}>
                        {WIKI_MODAL_COPY.cancel}
                    </button>
                    {footer}
                </div>
            </div>
        </div>,
        document.body,
    );
}

/** Uppercase field label + control column (prototype gap 7px; `group` 8px; `tight` 6px). */
export function WikiField({ label, required = false, htmlFor, variant, children, testId }) {
    const cls = `wk-modal-field${variant ? ` wk-modal-field--${variant}` : ''}`;
    const LabelTag = htmlFor ? 'label' : 'div';
    return (
        <div className={cls} data-testid={testId}>
            <LabelTag className="wk-modal-label wk-t-label" htmlFor={htmlFor}>
                {label}
                {required && <>{' '}<span className="wk-modal-req" aria-hidden="true">*</span></>}
            </LabelTag>
            {children}
        </div>
    );
}

/**
 * Prototype segmented control (`seg(on)`), as a radio group: arrow keys move
 * the selection, the active pill is white on the #EDEBE6 track.
 */
export function WikiSegmented({ options, value, onChange, label, block = false, testId }) {
    const items = options.map((option) => (typeof option === 'string' ? { id: option, label: option } : option));
    const onKeyDown = (event) => {
        if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
        event.preventDefault();
        const index = Math.max(0, items.findIndex((item) => item.id === value));
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const next = items[(index + step + items.length) % items.length];
        onChange(next.id);
        const buttons = event.currentTarget.querySelectorAll('[role="radio"]');
        const el = buttons[items.indexOf(next)];
        if (el) el.focus();
    };
    return (
        <div
            className={`wk-seg${block ? ' wk-seg--block' : ''}`}
            role="radiogroup"
            aria-label={label}
            onKeyDown={onKeyDown}
            data-testid={testId}
        >
            {items.map((item) => {
                const on = item.id === value;
                return (
                    <button
                        key={item.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        tabIndex={on ? 0 : -1}
                        className={`wk-seg-item${on ? ' is-on' : ''}`}
                        onClick={() => onChange(item.id)}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
}

/** Prototype toggle card: title + description + 36×20 teal switch. */
export function WikiToggleCard({ title, description, checked, onChange, testId }) {
    const titleId = useId();
    return (
        <div className="wk-toggle-card" data-testid={testId}>
            <div className="wk-toggle-text">
                <div id={titleId} className="wk-t-name">{title}</div>
                <div className="wk-toggle-desc wk-t-meta">{description}</div>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={Boolean(checked)}
                aria-labelledby={titleId}
                className={`wk-switch${checked ? ' is-on' : ''}`}
                onClick={() => onChange(!checked)}
            >
                <span className="wk-switch-knob" />
            </button>
        </div>
    );
}

/** Red alert box (prototype import error box); also used for failed creates. */
export function WikiModalError({ message, testId = 'wiki-modal-error' }) {
    if (!message) return null;
    return (
        <div className="wk-modal-error" role="alert" data-testid={testId}>
            <WikiIcon name="alert-triangle" size={14} />
            <span>{message}</span>
        </div>
    );
}

/**
 * Primary footer action (prototype `wkCreateStyle`). Stays focusable when it
 * can't act (`aria-disabled`) and ignores clicks then — the prototype's
 * "disabled clicks are silent no-ops".
 */
export function WikiCreateButton({ enabled, busy = false, onClick, icon, iconSize = 15, children, action, variant = 'primary', testId }) {
    const inert = !enabled || busy;
    // 'danger' (#872): the red outline button for Archive confirmations.
    const cls = {
        secondary: 'wk-btn wk-btn--secondary wk-modal-secondary',
        danger: 'wk-btn wk-btn--danger wk-modal-create',
    }[variant] || 'wk-btn wk-btn--primary wk-modal-create';
    return (
        <button
            type="button"
            className={cls}
            aria-disabled={!enabled ? 'true' : undefined}
            aria-busy={busy ? 'true' : undefined}
            data-action={action}
            data-testid={testId}
            onClick={() => { if (!inert) onClick(); }}
        >
            {icon && <WikiIcon name={icon} size={iconSize} />}
            {children}
        </button>
    );
}
